import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LookupSelect } from "@/components/app/LookupSelect";
import { SalespersonSelect } from "@/components/order/SalespersonSelect";
import { api, errMsg } from "@/lib/api";

const FIELDS = [["sales_owner", "Salesperson"], ["segment", "Customer segment"], ["preferred_transport_mode", "Preferred transport mode"], ["dispatch_instructions", "Dispatch instructions"]];

function Results({ res }) {
  return (
    <div data-testid="lm-bulk-results">
      <div className="flex flex-wrap gap-2 text-xs mb-2">{Object.entries(res.summary).map(([k, v]) => <span key={k} className="px-2 py-0.5 rounded border uppercase font-semibold" data-testid={`lm-bulk-summary-${k}`}>{k}: {v}</span>)}</div>
      <div className="max-h-60 overflow-auto border border-slate-200 rounded"><table className="w-full text-xs"><tbody>
        {res.results.map((r) => <tr key={r.row} className="border-b border-slate-100"><td className="px-2 py-1 font-mono">{r.name}</td><td className="px-2 py-1 uppercase font-mono">{r.status}</td><td className="px-2 py-1 text-slate-600">{r.message}</td></tr>)}
      </tbody></table></div>
    </div>
  );
}

// Preview -> confirm -> result summary, shared by both bulk actions
function useBulk(url, body, onDone, okMsg) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (commit) => {
    setBusy(true);
    try {
      const r = await api.post(`${url}?commit=${commit}`, body);
      setRes(r.data);
      if (commit) { toast.success(`${r.data.created} LM(s) ${okMsg}`); onDone(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return { res, setRes, busy, run };
}

function Footer({ b, ready, verb }) {
  return (
    <DialogFooter>
      {!b.res?.committed && <Button variant="outline" disabled={b.busy || !ready} onClick={() => b.run(false)} data-testid="lm-bulk-preview-btn">Preview</Button>}
      {b.res && !b.res.committed && <Button disabled={b.busy || !b.res.summary.ok} onClick={() => b.run(true)} data-testid="lm-bulk-apply-btn">Confirm - {verb} {b.res.summary.ok} LM(s)</Button>}
    </DialogFooter>
  );
}

export function LmBulkEditDialog({ ids, onClose, onDone }) {
  const [on, setOn] = useState({});
  const [v, setV] = useState({});
  const changes = Object.fromEntries(FIELDS.filter(([k]) => on[k]).map(([k]) => [k, v[k] || ""]));
  const b = useBulk("/orders/bulk-update", { ids, changes }, onDone, "updated");
  const set = (k, x) => { setV({ ...v, [k]: x }); b.setRes(null); };
  const input = (k) => {
    if (k === "sales_owner") return <SalespersonSelect value={v[k]} onChange={(x) => set(k, x)} testid="lm-bulk-sales_owner" />;
    if (k === "segment") return <LookupSelect type="segment" value={v[k]} onChange={(x) => set(k, x)} testid="lm-bulk-segment" />;
    if (k === "preferred_transport_mode") return <LookupSelect type="transport_mode" value={v[k]} onChange={(x) => set(k, x)} testid="lm-bulk-preferred_transport_mode" />;
    return <Textarea rows={2} value={v[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid="lm-bulk-dispatch_instructions" />;
  };
  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="lm-bulk-edit-dialog">
        <DialogHeader><DialogTitle>Bulk edit {ids.length} LM(s)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Only these low-risk fields can be bulk edited. Closed and cancelled LMs are skipped. Every change is recorded in each LM's audit trail.</p>
        {FIELDS.map(([k, l]) => (
          <div key={k} className="grid grid-cols-[auto_1fr] sm:grid-cols-[200px_1fr] items-start gap-2">
            <label className="flex items-center gap-2 text-sm pt-2"><input type="checkbox" checked={!!on[k]} onChange={() => { setOn({ ...on, [k]: !on[k] }); b.setRes(null); }} data-testid={`lm-bulk-pick-${k}`} />{l}</label>
            {on[k] ? input(k) : <span className="text-xs text-slate-400 pt-2">Not changed</span>}
          </div>
        ))}
        {b.res && <Results res={b.res} />}
        <Footer b={b} ready={Object.keys(changes).length > 0} verb="update" />
      </DialogContent>
    </Dialog>
  );
}

export function LmBulkStartDialog({ ids, onClose, onDone }) {
  const [rem, setRem] = useState("");
  const b = useBulk("/orders/bulk-start-execution", { ids, execution_remarks: rem }, onDone, "- execution started");
  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="lm-bulk-start-dialog">
        <DialogHeader><DialogTitle>Start Execution on {ids.length} selected LM(s)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Only approved LMs whose execution has not started are updated; others are listed as skipped. Each LM records who started it and when.</p>
        <div><Label>Execution remarks (optional)</Label><Input value={rem} onChange={(e) => { setRem(e.target.value); b.setRes(null); }} data-testid="lm-bulk-start-remarks" /></div>
        {b.res && <Results res={b.res} />}
        <Footer b={b} ready verb="start" />
      </DialogContent>
    </Dialog>
  );
}
